import { getBoxProgramColorTypeWithHex } from "@/modules/boxProgrammation/boxProgramUtils"
import { boxProgramColors } from "@/modules/boxProgrammation/components/boxProgramColors"
import type { BoxProgramDetailsEntity } from "@/modules/boxProgrammation/entities/boxProgramEntity"
import type { BoxProgramSlotDefaultSettingsEntity } from "@/modules/boxProgrammation/entities/boxProgramSlotDefaultSettingsEntity"
import type { BoxProgrammationLight } from "@/modules/boxProgrammation/gateway/boxProgramDto"
import {
  BOX_SLOT_DURATION_DEFAULT_VALUE,
  BOX_SLOT_PARTICIPANTS_DEFAULT_VALUE,
  type BoxSlotParticipation,
} from "@/modules/boxSlot/boxSlotTypes"
import { getWeekdayByIndex, Weekdays } from "@/modules/boxSlotTemplate/boxSlotTemplateTypes"
import { initializeDateUtils } from "@/modules/boxSlotTemplate/boxSlotUtils"
import type { BoxSlotTemplateFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateFormikEntity"
import type { BoxSlotTemplateGroupFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateGroupFormikEntity"
import { type BoxSlotTemplateDto } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateDto"
import { CalendarEventTypes, type SlotTemplateEventType } from "@/modules/calendar/calendarTypes"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import dayjs, { Dayjs } from "dayjs"
import { v4 as uuidv4 } from "uuid"

export class BoxSlotTemplateEntity {
  private constructor(
    public readonly id: string,
    public readonly program: BoxProgrammationLight,
    public readonly weekDay: Weekdays,
    public readonly startTime: string,
    public readonly endTime: string,
    public readonly participantLimit: number | null,
    public readonly location: string | null,
    public readonly participation: BoxSlotParticipation | null,
    public readonly allowMultiBooking: boolean,
    public readonly assignedStaffMembers: UserEntity[],
    public readonly dropinsAccepted: boolean,
    public readonly trialsAccepted: boolean,
  ) {}

  static fromDto(dto: BoxSlotTemplateDto): BoxSlotTemplateEntity {
    return new BoxSlotTemplateEntity(
      dto.id,
      dto.program,
      dto.weekDay,
      dto.startTime,
      dto.endTime,
      dto.participantLimit,
      dto.location,
      dto.participation,
      dto.allowMultiBooking,
      dto.assignedStaffMembers.map((staff) => UserEntity.fromDto(staff)),
      dto.dropinsAccepted,
      dto.trialsAccepted,
    )
  }

  static fromDefaultSettings(
    defaultSettings: BoxProgramSlotDefaultSettingsEntity,
    dateToCreateSlotTemplateOn: Dayjs,
    boxProgram: BoxProgramDetailsEntity,
  ): BoxSlotTemplateEntity {
    const boxProgramLight = {
      id: boxProgram.id,
      name: boxProgram.name,
      color: boxProgram.color,
      defaultHideSessionContent: boxProgram.defaultHideSessionContent,
      visibility: boxProgram.visibility,
    }

    const weekDay = getWeekdayByIndex(dateToCreateSlotTemplateOn.day())

    return new BoxSlotTemplateEntity(
      `draft-${uuidv4()}`,
      boxProgramLight,
      weekDay,
      dateToCreateSlotTemplateOn.format("HH:mm:ss"),
      dateToCreateSlotTemplateOn
        .add(defaultSettings.duration ?? BOX_SLOT_DURATION_DEFAULT_VALUE, "minute")
        .format("HH:mm:ss"),
      defaultSettings.participantLimit ?? BOX_SLOT_PARTICIPANTS_DEFAULT_VALUE,
      defaultSettings.location,
      defaultSettings.participation,
      defaultSettings.allowMultiBooking,
      defaultSettings.assignedStaffMembers,
      defaultSettings.dropinsAccepted,
      defaultSettings.trialsAccepted,
    )
  }

  static fromFormikEntity(
    formikEntity: BoxSlotTemplateFormikEntity,
    boxProgram: BoxProgramDetailsEntity,
  ): BoxSlotTemplateEntity {
    return new BoxSlotTemplateEntity(
      formikEntity.id,
      boxProgram,
      formikEntity.weekDay,
      dayjs(formikEntity.startDateTime).format("HH:mm:ss"),
      dayjs(formikEntity.endDateTime).format("HH:mm:ss"),
      formikEntity.participantLimit,
      formikEntity.location ?? null,
      formikEntity.limitStart && formikEntity.limitEnd && formikEntity.limitCancel
        ? {
            limitStart: formikEntity.limitStart,
            limitEnd: formikEntity.limitEnd,
            limitCancel: formikEntity.limitCancel,
          }
        : null,
      formikEntity.allowMultiBooking,
      formikEntity.assignedStaffMembers ?? [],
      formikEntity.dropinsAccepted,
      formikEntity.trialsAccepted,
    )
  }

  static fromGroupFormikEntity(
    formikEntity: BoxSlotTemplateGroupFormikEntity,
    boxProgram: BoxProgramDetailsEntity,
  ): BoxSlotTemplateEntity[] {
    return formikEntity.slotTemplatesRanges.flatMap((range, rangeIdx) => {
      const slots: BoxSlotTemplateEntity[] = []

      const start = dayjs(range.startTime)
      const end = dayjs(range.endTime)
      const slotDuration = formikEntity.slotDuration

      if (!slotDuration || slotDuration <= 0) return []

      let current = start
      let slotIndex = 0

      while (current.add(slotDuration, "minute").isSameOrBefore(end)) {
        const slotStart = current
        const slotEnd = current.add(slotDuration, "minute")
        slots.push(
          new BoxSlotTemplateEntity(
            `draft-${rangeIdx}-${slotIndex}`,
            boxProgram,
            range.weekDay,
            slotStart.format("HH:mm:ss"),
            slotEnd.format("HH:mm:ss"),
            formikEntity.participantLimit,
            formikEntity.location ?? null,
            formikEntity.limitStart && formikEntity.limitEnd && formikEntity.limitCancel
              ? {
                  limitStart: formikEntity.limitStart,
                  limitEnd: formikEntity.limitEnd,
                  limitCancel: formikEntity.limitCancel,
                }
              : null,
            formikEntity.allowMultiBooking,
            formikEntity.assignedStaffMembers,
            formikEntity.dropinsAccepted,
            formikEntity.trialsAccepted,
          ),
        )
        current = slotEnd
        slotIndex++
      }
      return slots
    })
  }

  toCalendarEventTypeWithHour(): SlotTemplateEventType {
    const colorType = getBoxProgramColorTypeWithHex(this.program.color)
    const programationColor = boxProgramColors[colorType]
    const { getDateTimeByAddingWeekday } = initializeDateUtils()

    const startDateTime = getDateTimeByAddingWeekday(this.startTime, this.weekDay)
    const endDateTime = getDateTimeByAddingWeekday(this.endTime, this.weekDay)

    return {
      id: this.id,
      programId: this.program.id,
      title: this.program.name,
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
      editable: false,
      leftBorderColor: programationColor.background,
      borderStyle: "dashed",
      assignedStaffMembers: this.assignedStaffMembers,
      color: "var(--surface-background)",
      borderColor: "var(--neutral-200)",
      textColor: "var(--neutral-900)",
      dayIndex: dayjs(startDateTime).weekday(),
      _type: CalendarEventTypes.SLOT_TEMPLATE_EVENT,
      participantLimit: this.participantLimit ?? undefined,
    }
  }
}
